import React, {useCallback, useEffect, useMemo, useState} from 'react'
import {useIntl} from 'react-intl'

import {AttendanceReportExportPayload} from '../../modes/attendance/attendanceReportExportTypes'
import AttendanceReportExportButtons from './attendance/AttendanceReportExportButtons'
import AttendanceV2DataTable, {
  ColumnDef,
  INITIAL_VISIBLE_ROWS,
} from './attendance/v2/AttendanceV2DataTable'
import {exportNamingFor, exportTextLines} from './markdownTables'

const LOAD_MORE_BATCH = 8

type MarkdownTableCardProps = {
  columns: string[]
  rows: Record<string, string>[]
  /** Answer text above the table (the lead sentence); written under the export's title. */
  narrative: string
  /** Answer text below the table (e.g. a closing note); written after the exported table. */
  footer: string
  /** The backend's capability for the answer (`fee.data`); names the export. */
  capability?: string | null
  theme: 'light' | 'dark'
}

/**
 * A pipe table from a bot answer (Versi fee lists), drawn as the same card as the attendance
 * results (`AttendanceV2ResultView`): the v2 data table, Load more / Show less in batches of 8,
 * and the Excel / PDF export buttons.
 */
const MarkdownTableCard: React.FC<MarkdownTableCardProps> = ({
  columns,
  rows,
  narrative,
  footer,
  capability,
  theme,
}) => {
  const intl = useIntl()
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE_ROWS)

  useEffect(() => {
    setVisibleCount(INITIAL_VISIBLE_ROWS)
  }, [rows.length])

  const columnDefs = useMemo<ColumnDef[]>(
    () => columns.map((column) => ({key: column, label: column})),
    [columns]
  )
  const visibleRows = useMemo(() => rows.slice(0, visibleCount), [rows, visibleCount])
  // Laid out like the attendance exports: a generic title, the chat reply in chat order, the
  // table, then whatever the answer said after it.
  const exportPayload = useMemo<AttendanceReportExportPayload>(
    () => ({
      title: intl.formatMessage({id: exportNamingFor(capability).titleId}),
      narrativeLines: exportTextLines(narrative),
      columns,
      rows,
      footerLines: exportTextLines(footer),
      filename: exportNamingFor(capability).filename,
    }),
    [capability, columns, footer, intl, narrative, rows]
  )

  const showLoadMore = rows.length > INITIAL_VISIBLE_ROWS && visibleCount < rows.length
  const remaining = rows.length - visibleCount
  const showLess = visibleCount > INITIAL_VISIBLE_ROWS
  const collapsible = Math.min(
    LOAD_MORE_BATCH,
    Math.min(visibleCount, rows.length) - INITIAL_VISIBLE_ROWS
  )

  const handleLoadMore = useCallback(() => {
    setVisibleCount((prev) => Math.min(prev + LOAD_MORE_BATCH, rows.length))
  }, [rows.length])

  const handleShowLess = useCallback(() => {
    setVisibleCount((prev) =>
      Math.max(Math.min(prev, rows.length) - LOAD_MORE_BATCH, INITIAL_VISIBLE_ROWS)
    )
  }, [rows.length])

  if (!columns.length || !rows.length) return null

  const borderColor = theme === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.1)'
  const cardBg = theme === 'dark' ? 'rgba(15, 23, 42, 0.94)' : 'rgba(255,255,255,0.65)'
  const buttonStyle = {
    fontSize: '0.75rem',
    whiteSpace: 'nowrap' as const,
    padding: '0.35rem 0.6rem',
  }

  return (
    <div
      className='attendance-v2-result mt-2 mb-1 rounded-3 p-2'
      style={{border: `1px solid ${borderColor}`, backgroundColor: cardBg}}
    >
      <AttendanceV2DataTable
        columns={columnDefs}
        rows={visibleRows}
        totalRows={rows.length}
        theme={theme}
      />
      <div className='d-flex justify-content-end align-items-center gap-1 mt-2 pt-1 flex-nowrap'>
        {showLess ? (
          <button
            type='button'
            className='btn btn-sm btn-light-primary'
            onClick={handleShowLess}
            style={buttonStyle}
          >
            {intl.formatMessage({id: 'CHATBOT.ATTENDANCE.SHOW_LESS'})}
            {` (${collapsible})`}
          </button>
        ) : null}
        {showLoadMore ? (
          <button
            type='button'
            className='btn btn-sm btn-light-primary'
            onClick={handleLoadMore}
            style={buttonStyle}
          >
            {intl.formatMessage({id: 'CHATBOT.ATTENDANCE.LOAD_MORE'})}
            {remaining > LOAD_MORE_BATCH ? ` (${LOAD_MORE_BATCH})` : ` (${remaining})`}
          </button>
        ) : null}
        <AttendanceReportExportButtons exportPayload={exportPayload} theme={theme} inline />
      </div>
    </div>
  )
}

export default MarkdownTableCard
